import { DEFAULT_TOOLTIP_POSITION, TOOLTIP_POSITIONS_PRIORITY } from './constants'
import { getIsTooltipFitsInPosition } from './getIsTooltipFitsInPosition'
import { getPositionedTipBoundaries } from './getPositionedTipBoundaries'
import { getTipOffset } from './getTipOffset'
import { getTipPoint } from './getTipPoint'
import { TooltipCoordinates, TooltipOffset, TooltipPosition, TooltipSize } from './types'

export interface Args {
  position: TooltipPosition
  targetBoundingRect: DOMRect
  tooltipSize: TooltipSize
  viewportSize: TooltipSize
}

export function getTipPositionData({
  position,
  targetBoundingRect,
  tooltipSize,
  viewportSize,
}: Args): {
  /**
   * The final position of the tooltip after considering viewport boundaries and main axis switching.
   */
  position: TooltipPosition
  /**
   * The point where the tooltip should be positioned, adjusted for the final position.
   */
  point: TooltipCoordinates
  /**
   * The offset needed to adjust the tooltip's position within the viewport.
   */
  offset: TooltipOffset
} {
  const tooltipPoint = getTipPoint(position, targetBoundingRect)
  const isFitsInPosition = getIsTooltipFitsInPosition({
    position,
    tooltipPoint,
    tooltipSize,
    viewportSize,
  })

  if (isFitsInPosition) {
    return {
      position,
      point: tooltipPoint,
      offset: getTipOffset(
        position,
        getPositionedTipBoundaries({ position, tooltipSize, tooltipPoint }),
        viewportSize,
      ),
    }
  }

  for (const newPosition of TOOLTIP_POSITIONS_PRIORITY) {
    const tooltipPoint = getTipPoint(newPosition, targetBoundingRect)
    const isFitsInFallbackPosition = getIsTooltipFitsInPosition({
      position: newPosition,
      tooltipPoint,
      tooltipSize,
      viewportSize,
    })

    if (isFitsInFallbackPosition) {
      return {
        position: newPosition,
        point: tooltipPoint,
        offset: getTipOffset(
          newPosition,
          getPositionedTipBoundaries({ position: newPosition, tooltipSize, tooltipPoint }),
          viewportSize,
        ),
      }
    }
  }

  const fallbackPosition = position || DEFAULT_TOOLTIP_POSITION

  return {
    position: fallbackPosition,
    point: getTipPoint(fallbackPosition, targetBoundingRect),
    offset: getTipOffset(
      fallbackPosition,
      getPositionedTipBoundaries({
        position: fallbackPosition,
        tooltipSize,
        tooltipPoint: getTipPoint(fallbackPosition, targetBoundingRect),
      }),
      viewportSize,
    ),
  }
}
